/**
 * MapEditToolbar.jsx - Barre d'outils d'édition pour la carte interactive
 *
 * Permet de :
 * - Basculer entre mode Consultation et Édition
 * - Sélectionner un capteur à placer
 * - Sélectionner un secteur à délimiter
 * - Sauvegarder les modifications
 */

import React from 'react';
import {
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  Chip,
  Divider,
  IconButton
} from '@mui/material';
import {
  Edit,
  Visibility,
  Save,
  Cancel,
  LocationOn,
  Hexagon,
  CheckCircle,
  MyLocation,
  PlayArrow,
  Stop,
  UploadFile
} from '@mui/icons-material';

/**
 * Composant de la barre d'outils d'édition
 */
const MapEditToolbar = ({
  editMode,
  onEditModeChange,
  allSensors = [],
  allSectors = [],
  sensorsWithoutGPS = [],
  sectorsWithoutBoundary = [],
  selectedSensorToPlace,
  selectedSectorToDraw,
  onSensorSelect,
  onSectorSelect,
  onSave,
  onCancel,
  hasUnsavedChanges,
  onUseMyLocationForSensor,
  onUseMyLocationForSector,
  isRecordingPerimeter,
  onStartPerimeter,
  onStopPerimeter,
  onImportBoundaryFile,
  onOpenAddSensor,
  onOpenAddSector,
  isFullscreen = false
}) => {
  // Debug: Tracer les props reçues avec structure détaillée
  console.log('🔧 MapEditToolbar - Props reçues:', {
    editMode,
    allSensors_count: allSensors.length,
    allSectors_count: allSectors.length,
    allSensors_preview: allSensors.slice(0, 2),
    allSectors_preview: allSectors.slice(0, 2),
    first_sensor_keys: allSensors[0] ? Object.keys(allSensors[0]) : [],
    first_sector_keys: allSectors[0] ? Object.keys(allSectors[0]) : [],
    first_sensor_full: allSensors[0],
    first_sector_full: allSectors[0]
  });

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'absolute',
        top: isFullscreen ? 10 : 70,
        right: 10,
        zIndex: 1500,
        p: 2,
        bgcolor: 'rgba(255, 255, 255, 0.98)',
        minWidth: 300,
        maxWidth: 350,
        maxHeight: isFullscreen ? 'calc(100vh - 20px)' : 'calc(100vh - 80px)',
        overflowY: 'auto'
      }}
    >
      {/* Mode Toggle */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
          Mode de la carte
        </Typography>
        <ToggleButtonGroup
          value={editMode}
          exclusive
          onChange={(e, newMode) => newMode && onEditModeChange(newMode)}
          fullWidth
          size="small"
        >
          <ToggleButton value="view" aria-label="mode consultation">
            <Visibility sx={{ mr: 1 }} fontSize="small" />
            Consulter
          </ToggleButton>
          <ToggleButton value="edit" aria-label="mode édition">
            <Edit sx={{ mr: 1 }} fontSize="small" />
            Éditer
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Outils d'édition - Affichés seulement en mode édition */}
      {editMode === 'edit' && (
        <>
          <Divider sx={{ my: 2 }} />

          {/* Placement des capteurs */}
          <Box sx={{ mb: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="sensor-select-label">
                <LocationOn fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                Placer un capteur
              </InputLabel>
              <Select
                labelId="sensor-select-label"
                value={selectedSensorToPlace !== null ? String(selectedSensorToPlace) : ''}
                label="Placer un capteur"
                MenuProps={{ sx: { zIndex: 2000 }, PaperProps: { sx: { zIndex: 2000 } } }}
                onChange={(e) => onSensorSelect(e.target.value)}
              >
                <MenuItem value="">
                  <em>Sélectionner un capteur</em>
                </MenuItem>
                {allSensors
                  .filter(sensor => sensor && (sensor.source_id || sensor.sensorId || sensor.id))
                  .map((sensor) => {
                    const sensorId = sensor.source_id || sensor.sensorId || sensor.id;
                    return (
                      <MenuItem key={sensorId} value={String(sensorId)}>
                        {sensorId}
                        {sensor.sensor_type && ` (${sensor.sensor_type})`}
                        {sensor.gps_latitude && sensor.gps_longitude && ' ✓'}
                        {sensor.sector_id && ` - Secteur ${sensor.sector_id}`}
                      </MenuItem>
                    );
                  })
                }
              </Select>
            </FormControl>
            {sensorsWithoutGPS.length === 0 && (
              <Typography variant="caption" color="success.main" sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <CheckCircle fontSize="small" sx={{ mr: 0.5 }} />
                Tous les capteurs sont placés
              </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 1, flexWrap: 'wrap' }}>
              {selectedSensorToPlace && (
                <Typography variant="caption" color="primary">
                  📍 Cliquez sur la carte ou utilisez votre position
                </Typography>
              )}
              <IconButton
                size="small"
                color="primary"
                title="Utiliser ma position (GPS)"
                onClick={onUseMyLocationForSensor}
                disabled={!selectedSensorToPlace}
              >
                <MyLocation fontSize="small" />
              </IconButton>
              <Button variant="outlined" size="small" onClick={onOpenAddSensor}>
                Nouveau capteur
              </Button>
            </Box>
          </Box>

          {/* Dessin des secteurs */}
          <Box sx={{ mb: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="sector-select-label">
                <Hexagon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                Délimiter un secteur
              </InputLabel>
              <Select
                labelId="sector-select-label"
                value={selectedSectorToDraw !== null ? String(selectedSectorToDraw) : ''}
                label="Délimiter un secteur"
                MenuProps={{ sx: { zIndex: 2000 }, PaperProps: { sx: { zIndex: 2000 } } }}
                onChange={(e) => onSectorSelect(e.target.value)}
              >
                <MenuItem value="">
                  <em>Sélectionner un secteur</em>
                </MenuItem>
                {allSectors
                  .filter(sector => sector && (sector.sector_id || sector.sectorId || sector.id))
                  .map((sector) => {
                    const sectorId = sector.sector_id || sector.sectorId || sector.id;
                    const hasBoundary = sector.boundary_polygon || sector.boundaryPolygon;
                    const compartmentName = sector.compartment_name || sector.compartmentName;
                    return (
                      <MenuItem key={sectorId} value={String(sectorId)}>
                        {sector.name || sectorId}
                        {hasBoundary && ' ✓'}
                        {compartmentName && ` - ${compartmentName}`}
                      </MenuItem>
                    );
                  })
                }
              </Select>
            </FormControl>
            {sectorsWithoutBoundary.length === 0 && (
              <Typography variant="caption" color="success.main" sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <CheckCircle fontSize="small" sx={{ mr: 0.5 }} />
                Tous les secteurs sont délimités
              </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 1, flexWrap: 'wrap' }}>
              {selectedSectorToDraw && (
                <Typography variant="caption" color="primary">
                  ✏️ Recentrer sur ma position puis tracer le polygone
                </Typography>
              )}
              <IconButton
                size="small"
                color="primary"
                title="Recentrer sur ma position (GPS)"
                onClick={onUseMyLocationForSector}
                disabled={!selectedSectorToDraw}
              >
                <MyLocation fontSize="small" />
              </IconButton>

              {/* Enregistrement périmètre GPS (mobile) */}
              <IconButton
                size="small"
                color={isRecordingPerimeter ? 'error' : 'primary'}
                title={isRecordingPerimeter ? 'Arrêter enregistrement périmètre' : 'Démarrer enregistrement périmètre (GPS)'}
                onClick={isRecordingPerimeter ? onStopPerimeter : onStartPerimeter}
                disabled={!selectedSectorToDraw}
              >
                {isRecordingPerimeter ? <Stop fontSize="small" /> : <PlayArrow fontSize="small" />}
              </IconButton>

              {/* Import GeoJSON/KML */}
              <label style={{ display: 'inline-flex' }}>
                <input
                  type="file"
                  accept=".geojson,.json,.kml,application/vnd.google-earth.kml+xml,application/json"
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      const text = await file.text();
                      onImportBoundaryFile && onImportBoundaryFile(text, file.name);
                    } catch (_) {}
                    e.target.value = '';
                  }}
                />
                <IconButton size="small" color="primary" title="Importer GeoJSON/KML" disabled={!selectedSectorToDraw} component="span">
                  <UploadFile fontSize="small" />
                </IconButton>
              </label>

              <Button variant="outlined" size="small" onClick={onOpenAddSector}>
                Nouveau secteur
              </Button>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Boutons de sauvegarde/annulation */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Save />}
              onClick={onSave}
              disabled={!hasUnsavedChanges}
              fullWidth
            >
              Sauvegarder
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<Cancel />}
              onClick={onCancel}
              fullWidth
            >
              Annuler
            </Button>
          </Box>

          {hasUnsavedChanges && (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
              ⚠️ Modifications non sauvegardées
            </Typography>
          )}
        </>
      )}

      {/* Statistiques en mode consultation */}
      {editMode === 'view' && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Capteurs sans GPS:
              </Typography>
              <Chip
                label={sensorsWithoutGPS.length}
                size="small"
                color={sensorsWithoutGPS.length > 0 ? 'warning' : 'success'}
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Secteurs sans délimitation:
              </Typography>
              <Chip
                label={sectorsWithoutBoundary.length}
                size="small"
                color={sectorsWithoutBoundary.length > 0 ? 'warning' : 'success'}
              />
            </Box>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default MapEditToolbar;
